/**
 * Compress a launcher wallpaper for local persistence.
 * Firebase Storage uploads from app.signalbridges.com are blocked (CORS/403),
 * so Settings stores a JPEG data URL in localStorage instead.
 */

export const ALLOWED_LAUNCHER_BACKGROUND_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
]);

export const MAX_LAUNCHER_BACKGROUND_BYTES = 10 * 1024 * 1024;
export const MAX_INLINE_BACKGROUND_CHARS = 280_000;

const COMPRESS_ATTEMPTS = [
  { maxEdge: 1280, quality: 0.78 },
  { maxEdge: 1024, quality: 0.68 },
  { maxEdge: 800, quality: 0.58 },
  { maxEdge: 640, quality: 0.48 },
  { maxEdge: 480, quality: 0.38 },
];

export const isInlineBackgroundUrl = (url) => String(url || '').startsWith('data:');

export const layoutSafeBackgroundUrl = (url) => (
  isInlineBackgroundUrl(url) ? '' : (url || '')
);

export const assertLauncherBackgroundFile = (file) => {
  if (!file) throw new Error('Choose an image file.');
  const type = file.type || inferImageMimeType(file.name);
  if (!ALLOWED_LAUNCHER_BACKGROUND_TYPES.has(type)) {
    throw new Error(`Unsupported file type: ${type || 'unknown'}. Use JPEG, PNG, WebP, or GIF.`);
  }
  if (file.size > MAX_LAUNCHER_BACKGROUND_BYTES) {
    throw new Error('File too large (max 10 MB).');
  }
};

const inferImageMimeType = (name = '') => {
  const ext = String(name).split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'gif':
      return 'image/gif';
    default:
      return '';
  }
};

const withTimeout = (promise, ms, message) => (
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(message)), ms);
    }),
  ])
);

const rasterizeToJpeg = async (file, { maxEdge, quality }) => {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    try {
      const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height, 1));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not prepare the image.');
      ctx.drawImage(bitmap, 0, 0, width, height);
      return canvas.toDataURL('image/jpeg', quality);
    } finally {
      bitmap.close?.();
    }
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      try {
        const scale = Math.min(1, maxEdge / Math.max(image.width, image.height, 1));
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not prepare the image.');
        ctx.drawImage(image, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read that image.'));
    };
    image.src = objectUrl;
  });
};

const compressLauncherBackgroundInner = async (file) => {
  let last = '';
  let lastError = null;
  for (const attempt of COMPRESS_ATTEMPTS) {
    try {
      last = await rasterizeToJpeg(file, attempt);
      if (last && last.length <= MAX_INLINE_BACKGROUND_CHARS) return last;
    } catch (err) {
      lastError = err;
    }
  }
  if (last && last.length <= MAX_INLINE_BACKGROUND_CHARS * 1.2) return last;
  throw lastError || new Error('Image is too large to save on this device. Try a smaller photo.');
};

export const compressLauncherBackground = async (file) => {
  assertLauncherBackgroundFile(file);
  return withTimeout(
    compressLauncherBackgroundInner(file),
    20000,
    'Image processing timed out. Try a smaller photo.',
  );
};
